/**
 * Order-level rollup status for DISPLAY only.
 *
 * The stored `orders.status` cannot express "leg 1 confirmed, leg 2 still
 * booked" — a real state produced by per-leg confirmation. This module derives
 * the status a user should see from the legs themselves, adding one extra
 * display-only value: `partially_confirmed`.
 *
 * Nothing here is ever persisted, sent to EAPI, or used for filtering/emails.
 * Those all continue to read `orders.status`.
 *
 * Rules (canceled legs are excluded first):
 *  - no active legs at all            → the order's own stored status
 *  - every active leg canceled        → canceled
 *  - all legs delivered               → delivered
 *  - any leg in_transit, or some (not all) delivered → in_transit
 *  - all legs confirmed (or beyond confirmed but not started) → confirmed
 *  - at least one confirmed, some still booked → partially_confirmed
 *  - otherwise                        → booked
 *
 * A leg with no resolvable status counts as `booked` — fail closed so an
 * unknown leg can never drag the rollup forward.
 */
import { resolveLegStatus, type ResolvableLeg } from "@/lib/leg-status-resolve";
import type { OrderStatus } from "@/types/tms";

export const PARTIALLY_CONFIRMED = "partially_confirmed" as const;

export type RollupStatus = OrderStatus | typeof PARTIALLY_CONFIRMED;

export type RollupLeg = ResolvableLeg;

/**
 * Status of a single leg, using only the leg's own signals.
 *
 * The parent status is deliberately NOT fed in here: on a multi-leg order it is
 * itself an aggregate, so letting it contribute would make an unstarted leg
 * read `confirmed` and hide the partially-confirmed state we're trying to show.
 */
function legOwnStatus(leg: RollupLeg): string {
  return resolveLegStatus(leg, undefined, true) ?? "booked";
}

export function resolveOrderRollupStatus(
  orderStatus: OrderStatus | string | null | undefined,
  legs: RollupLeg[] | null | undefined,
): RollupStatus {
  const fallback = (orderStatus ?? "booked") as RollupStatus;
  const all = legs ?? [];
  if (all.length === 0) return fallback;

  // Single-leg orders: the stored order status IS that leg's status (the leg
  // row often carries no driver_status of its own). There is no aggregate to
  // unpick, so never let the leg drag the chip back to "booked".
  if (all.length === 1) {
    return (resolveLegStatus(all[0], orderStatus, false) ?? fallback) as RollupStatus;
  }


  const statuses = all.map(legOwnStatus);
  const active = statuses.filter((s) => s !== "canceled");
  if (active.length === 0) return "canceled";

  const count = (s: string) => active.filter((x) => x === s).length;
  const delivered = count("delivered");
  const inTransit = count("in_transit");
  const confirmedOrBeyond = active.filter(
    (s) => s === "confirmed" || s === "tender_received" || s === "in_transit" || s === "delivered",
  ).length;

  if (delivered === active.length) return "delivered";
  if (inTransit > 0 || delivered > 0) return "in_transit";
  if (confirmedOrBeyond === active.length) return "confirmed";
  if (confirmedOrBeyond > 0) return PARTIALLY_CONFIRMED;
  return "booked";
}

export const ROLLUP_STATUS_LABEL_OVERRIDES: Record<string, string> = {
  [PARTIALLY_CONFIRMED]: "Partially Confirmed",
};
